import { c as _c } from "react/compiler-runtime";
import { formatMoney, parseCents, sanitizeMoney } from 'betterplace-utils';
import classNames from 'classnames';
import { type ChangeEvent, type FocusEvent, forwardRef, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForwardedRef } from '../../hooks';
import { TextInput } from '../TextInput';
import styles from './AmountField.module.css';
import { AmountFieldProps } from './types';
function normalizeConstraint(value: number | string | undefined) {
  if (typeof value === 'undefined') return;
  let result: number;
  if (typeof value !== 'number') {
    result = parseFloat(value);
  } else {
    result = value;
  }
  if (isNaN(result)) return;
  return Math.round(result * 100);
}
export function useExternalValue(value, defaultValue, locale, precision) {
  const $ = _c(11);
  let t0;
  if ($[0] !== defaultValue || $[1] !== locale || $[2] !== precision || $[3] !== value) {
    t0 = sanitizeMoney(formatMoney(locale, value ?? defaultValue ?? "", {
      precision,
      format: "%v",
      thousandSeparator: false
    }), precision);
    $[0] = defaultValue;
    $[1] = locale;
    $[2] = precision;
    $[3] = value;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const [localValue, setLocalValue] = useState(t0);
  const [prevValue, setPrevValue] = useState(value);
  let t1;
  if ($[5] !== defaultValue || $[6] !== localValue) {
    t1 = defaultValue ? {
      defaultValue: localValue
    } : {
      value: localValue
    };
    $[5] = defaultValue;
    $[6] = localValue;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const valueProps = t1;
  let t2;
  if ($[8] !== localValue || $[9] !== valueProps) {
    t2 = {
      valueProps,
      setLocalValue,
      localValue
    };
    $[8] = localValue;
    $[9] = valueProps;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const result = t2;
  if (typeof defaultValue !== "undefined") {
    return result;
  }
  const v = value ?? undefined;
  if (v === prevValue) {
    return result;
  }
  setPrevValue(v);
  if (v === undefined) {
    return result;
  }
  const update = sanitizeMoney(formatMoney(locale, v, {
    precision,
    format: "%v",
    thousandSeparator: false
  }), precision);
  if (update === localValue) {
    return result;
  }
  setLocalValue(update);
  return result;
}
const ClientAmountField = forwardRef<HTMLInputElement, AmountFieldProps & {
  locale?: string;
}>(({
  label,
  locale = 'de',
  name,
  id,
  min: min_ = 0,
  onChange: onChange_,
  onBlur: onBlur_,
  onFocus: onFocus_,
  defaultValue,
  value,
  step,
  children,
  className,
  hideSteps,
  max: max_,
  ...props
}, ref) => {
  const precision = useMemo(() => getPrecisionFromStep(step), [step]);
  const {
    valueProps,
    setLocalValue,
    localValue
  } = useExternalValue(value, defaultValue, locale, precision);
  const max = useMemo(() => normalizeConstraint(max_), [max_]);
  const min = useMemo(() => normalizeConstraint(min_), [min_]);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const inputRef = useForwardedRef(ref);
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const formatted = sanitizeAmount(event.target.value, locale, precision);
    const localParsed = parseCents(formatted);
    if (typeof max !== 'undefined' && localParsed > max || typeof min !== 'undefined' && localParsed < min) {
      const nextPosition = event.target.selectionStart ? event.target.selectionStart : 0;
      setCursorPosition(nextPosition);
      return event.preventDefault();
    }
    setCursorPosition(event.target.selectionStart);
    let next = event.target.value;
    if (!formatted.startsWith(event.target.value)) {
      next = formatted;
    }
    const parsed = parseCents(event.target.value);
    if (event.target.value && !isNaN(parsed) && localParsed === parsed) {
      onChange_?.(event);
    }
    setLocalValue(next);
  }, [locale, precision, max, min, setLocalValue, onChange_]);
  const onBlur = useCallback((event_0: FocusEvent<HTMLInputElement>) => {
    const sanitized = sanitizeAmount(event_0.target.value, locale, precision);
    if (sanitized !== event_0.target.value) {
      event_0.target.value = sanitized;
    }
    setCursorPosition(null);
    onBlur_?.(event_0);
  }, [onBlur_, precision, locale]);
  const onFocus = useCallback((event_1: FocusEvent<HTMLInputElement>) => {
    setCursorPosition(event_1.target.selectionStart);
    onFocus_?.(event_1);
  }, [onFocus_]);
  useEffect(() => {
    const valueLen = localValue?.length ?? 0;
    const correctedPosition = (cursorPosition ?? 0) > valueLen ? valueLen : cursorPosition;
    if (cursorPosition === null || !inputRef.current || document.activeElement !== inputRef.current) return;
    inputRef.current.setSelectionRange(correctedPosition, correctedPosition);
  }, [cursorPosition, inputRef, localValue]);
  return <TextInput {...props} {...valueProps} onFocus={onFocus} onBlur={onBlur} onChange={onChange} step={step} ref={inputRef} id={id || name} name={name} label={label} pattern="[0-9]*[.,]?[0-9]*" type="text" inputMode="decimal" className={classNames(className, {
    [styles.hideSteps!]: hideSteps
  })}>
        {children}
      </TextInput>;
});
ClientAmountField.displayName = 'ClientAmountField';
export function sanitizeAmount<T extends number | string | undefined>(value: T, locale: string, precision = 2) {
  if (typeof value === 'undefined') {
    return value;
  }
  if (value === null) {
    return value;
  }
  const valueStr = String(value);
  const sanitizedValue = sanitizeMoney(valueStr, precision);
  const formattedValue = formatMoney(locale, sanitizedValue, {
    precision,
    format: '%v',
    thousandSeparator: false
  });
  const sane = sanitizeMoney(formattedValue, precision);
  if (!precision) {
    return sane;
  }
  const separatorIndex = sane.search(/[.,]/);
  if (separatorIndex < 0) {
    return sane;
  }
  const toPad = precision - (sane.length - 1 - separatorIndex);
  return sane.concat('0'.repeat(toPad));
}
export function getPrecisionFromStep(step: string | number | undefined): number | undefined {
  if (step === undefined) {
    return;
  }
  const result = String(step).split('.')[1]?.length ?? 0;
  return result;
}
export default memo(ClientAmountField);